import React, { useState, useEffect } from 'react';
import { api, useAuth } from '../App';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft, Save, Plus, LayoutDashboard, FileText, Users, Loader2,
  Settings, Trash2, LogIn, UserPlus, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';
import ClientUTMTab from '../components/ClientUTMTab';

const PLATFORM_META = [
  { key: 'facebook', label: 'Facebook', color: '#1877F2', desc: 'Pages, posts, engagement', category: 'organic' },
  { key: 'instagram', label: 'Instagram', color: '#E4405F', desc: 'Reels, stories, feed', category: 'organic' },
  { key: 'linkedin', label: 'LinkedIn', color: '#0A66C2', desc: 'Company page, posts', category: 'organic' },
  { key: 'tiktok', label: 'TikTok', color: '#000000', desc: 'Videos, views, engagement', category: 'organic' },
  { key: 'youtube', label: 'YouTube', color: '#FF0000', desc: 'Videos, shorts, subscribers', category: 'organic' },
];

const PAID_SERVICES = [
  { key: 'facebook_ads', label: 'Facebook Ads', color: '#1877F2', desc: 'Paid campaigns, ad spend, conversions' },
  { key: 'google_ads', label: 'Google Ads', color: '#4285F4', desc: 'Search, display, video campaigns' },
];

export default function ClientDetailPage({ clientId, navigate }) {
  const { user: currentUser } = useAuth();
  const [client, setClient] = useState(null);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', full_name: '', role: 'client_viewer' });
  const [beehiivEditing, setBeehiivEditing] = useState(false);
  const [form, setForm] = useState({
    name: '', industry: '', logo_url: '', primary_color: '#1f2a4d', accent_color: '#e01e25', platforms: [], paid_services: [],
    beehiiv_api_key: '', beehiiv_pub_id: '', beehiiv_enabled: false, fb_ad_account_id: '', google_ads_customer_id: ''
  });

  const fetchData = async () => {
    try {
      const [clientData, usersData] = await Promise.all([
        api(`/api/clients/${clientId}`),
        api('/api/auth/users'),
      ]);
      setClient(clientData);
      setAllUsers(usersData);
      setUsers(usersData.filter(u => u.client_id === clientId));
      setForm({
        name: clientData.name || '',
        industry: clientData.industry || '',
        logo_url: clientData.logo_url || '',
        primary_color: clientData.primary_color || '#1f2a4d',
        accent_color: clientData.accent_color || '#e01e25',
        platforms: clientData.platforms || [],
        paid_services: clientData.paid_services || [],
        beehiiv_api_key: clientData.beehiiv_api_key || '',
        beehiiv_pub_id: clientData.beehiiv_pub_id || '',
        beehiiv_enabled: clientData.beehiiv_enabled || false,
        fb_ad_account_id: clientData.fb_ad_account_id || '',
        google_ads_customer_id: clientData.google_ads_customer_id || '',
      });
    } catch (err) {
      toast.error('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (clientId) fetchData(); }, [clientId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api(`/api/clients/${clientId}`, {
        method: 'PUT',
        body: JSON.stringify(form)
      });
      toast.success('Client settings saved');
      fetchData();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const togglePlatform = (p) => {
    setForm(prev => ({
      ...prev,
      platforms: prev.platforms.includes(p) ? prev.platforms.filter(x => x !== p) : [...prev.platforms, p]
    }));
  };

  const togglePaidService = (s) => {
    setForm(prev => ({
      ...prev,
      paid_services: prev.paid_services.includes(s) ? prev.paid_services.filter(x => x !== s) : [...prev.paid_services, s]
    }));
  };

  const handleAddUser = async () => {
    try {
      const payload = { ...newUser, client_id: clientId };
      // Use email as username if no username set
      if (!payload.username && payload.email) {
        payload.username = payload.email;
      }
      const result = await api('/api/auth/users', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      
      // Show invite link if available
      if (result.invite_link) {
        const fullLink = `${window.location.origin}${result.invite_link}`;
        toast.success(
          <div>
            <p className="font-medium">User created!</p>
            <p className="text-xs mt-1">Invite link copied to clipboard</p>
          </div>,
          { duration: 8000 }
        );
        navigator.clipboard.writeText(fullLink).catch(() => {});
      } else {
        toast.success('User added to client');
      }
      
      setAddUserOpen(false);
      setNewUser({ username: '', email: '', password: '', full_name: '', role: 'client_viewer' });
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleLoginAs = async () => {
    try {
      const data = await api(`/api/auth/impersonate/${clientId}`, { method: 'POST' });
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      toast.success(`Viewing as ${client?.name}`);
      window.location.reload();
    } catch (err) {
      toast.error(err.message);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="animate-fade-in-up">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" className="min-h-11 min-w-11 shrink-0" data-testid="client-detail-back" onClick={() => navigate('clients')}>
            <ArrowLeft size={18} />
          </Button>
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 shrink-0 rounded-xl flex items-center justify-center text-white font-bold text-lg"
              style={{ background: form.primary_color }}>
              {form.name?.[0] || '?'}
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight break-words" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {form.name}
              </h1>
              <p className="text-xs text-muted-foreground">{form.industry}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 [&>button]:min-h-11">
          <Button variant="outline" size="sm" data-testid="client-detail-dashboard" onClick={() => navigate('dashboard', { clientId })}>
            <LayoutDashboard size={14} className="mr-2" /> Dashboard
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('reports', { clientId })}>
            <FileText size={14} className="mr-2" /> Reports
          </Button>
          {currentUser?.role === 'super_admin' && (
            <Button size="sm" onClick={handleLoginAs} style={{ background: '#e01e25' }}>
              <LogIn size={14} className="mr-2" /> Login As Client
            </Button>
          )}
        </div>
      </div>

      {client?.metricool_blog_id && ['super_admin', 'internal'].includes(currentUser?.role) && !currentUser?.impersonating && (
        <Card className="mb-6 border-primary/20 bg-primary/5" data-testid="client-metricool-pilot-card"><CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"><div className="flex items-start gap-3"><BarChart3 size={20} className="text-primary shrink-0 mt-0.5" /><div><p className="text-sm font-semibold" data-testid="client-metricool-pilot-title">Metricool reporting</p><p className="text-xs text-muted-foreground mt-1" data-testid="client-metricool-pilot-note">Primary reporting source for organic social and Facebook Ads. Your stored legacy history stays available.</p></div></div><Button variant="outline" className="min-h-11 shrink-0" onClick={() => navigate('metricool', { clientId })} data-testid="client-open-metricool">Open sync & coverage</Button></CardContent></Card>
      )}

      <Tabs defaultValue="settings">
        <TabsList className="mb-6 grid grid-cols-2 sm:inline-flex h-auto gap-1 w-full sm:w-auto [&>button]:min-h-11">
          <TabsTrigger value="settings"><Settings size={14} className="mr-1.5" /> Settings</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="integrations-tab">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5"><path d="M12 22v-5"/><path d="M9 8V2"/><path d="M15 8V2"/><path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z"/></svg>
            Integrations
          </TabsTrigger>
          <TabsTrigger value="users"><Users size={14} className="mr-1.5" /> Users ({users.length})</TabsTrigger>
          <TabsTrigger value="utms">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1.5"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            UTMs
          </TabsTrigger>
        </TabsList>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Basic Info */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Client Info</h3>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Client Name</Label>
                    <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Industry</Label>
                    <Input value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Logo URL</Label>
                    <Input value={form.logo_url} onChange={e => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Primary Color</Label>
                      <div className="flex gap-2 items-center">
                        <input type="color" value={form.primary_color} onChange={e => setForm({ ...form, primary_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer" />
                        <Input value={form.primary_color} onChange={e => setForm({ ...form, primary_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Accent Color</Label>
                      <div className="flex gap-2 items-center">
                        <input type="color" value={form.accent_color} onChange={e => setForm({ ...form, accent_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer" />
                        <Input value={form.accent_color} onChange={e => setForm({ ...form, accent_color: e.target.value })} className="flex-1" />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Connected Services */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Connected Services</h3>
                <p className="text-xs text-muted-foreground mb-4">Toggle platforms on/off — disabled ones won't show in dashboards or reports</p>
                <div className="space-y-2">
                  {PLATFORM_META.map(p => {
                    const on = form.platforms.includes(p.key);
                    return (
                      <div key={p.key} onClick={() => togglePlatform(p.key)}
                        className={`flex items-center justify-between p-3 rounded-xl border-2 cursor-pointer transition-all ${
                          on ? 'border-[#1f2a4d] bg-[#1f2a4d]/5' : 'border-transparent bg-muted/30 hover:bg-muted/50'
                        }`} data-testid={`platform-toggle-${p.key}`}>
                        <div className="flex items-center gap-3">
                          <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                            style={{ background: on ? p.color : '#ccc' }}>
                            {p.label[0]}
                          </div>
                          <div>
                            <div className="text-sm font-medium">{p.label}</div>
                            <div className="text-xs text-muted-foreground">{p.desc}</div>
                          </div>
                        </div>
                        <div className={`w-9 h-5 rounded-full transition-colors flex items-center ${on ? 'bg-[#1f2a4d] justify-end' : 'bg-gray-300 justify-start'}`}>
                          <div className="w-3.5 h-3.5 rounded-full bg-white mx-0.5 shadow-sm" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Paid Advertising */}
          <Card className="mt-6">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Paid Advertising</h3>
              <p className="text-xs text-muted-foreground mb-4">Enable paid ad reporting for this client</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {PAID_SERVICES.map(s => {
                  const on = (form.paid_services || []).includes(s.key);
                  return (
                    <div key={s.key} onClick={() => togglePaidService(s.key)}
                      className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        on ? 'border-[#e01e25] bg-red-50/30' : 'border-transparent bg-muted/30 hover:bg-muted/50'
                      }`} data-testid={`paid-toggle-${s.key}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                          style={{ background: on ? s.color : '#ccc' }}>
                          {s.label.split(' ')[0][0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{s.label}</div>
                          <div className="text-xs text-muted-foreground">{s.desc}</div>
                        </div>
                      </div>
                      <div className={`w-9 h-5 rounded-full transition-colors flex items-center ${on ? 'bg-[#e01e25] justify-end' : 'bg-gray-300 justify-start'}`}>
                        <div className="w-3.5 h-3.5 rounded-full bg-white mx-0.5 shadow-sm" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Other Integrations Toggles */}
          <Card className="mt-6">
            <CardContent className="p-6">
              <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Other Integrations</h3>
              <p className="text-xs text-muted-foreground mb-4">Enable additional data sources for this client</p>
              <div
                onClick={() => setForm({ ...form, beehiiv_enabled: !form.beehiiv_enabled })}
                className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  form.beehiiv_enabled ? 'border-orange-500 bg-orange-50/30' : 'border-transparent bg-muted/30 hover:bg-muted/50'
                }`}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold"
                    style={{ background: form.beehiiv_enabled ? '#f97316' : '#ccc' }}>B</div>
                  <div>
                    <div className="text-sm font-medium">Beehiiv</div>
                    <div className="text-xs text-muted-foreground">Newsletter analytics & subscriber data</div>
                  </div>
                </div>
                <div className={`w-9 h-5 rounded-full transition-colors flex items-center ${form.beehiiv_enabled ? 'bg-orange-500 justify-end' : 'bg-gray-300 justify-start'}`}>
                  <div className="w-3.5 h-3.5 rounded-full bg-white mx-0.5 shadow-sm" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="mt-6 flex justify-end">
            <Button onClick={handleSave} disabled={saving} style={{ background: '#1f2a4d' }} data-testid="save-client-settings">
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Save Settings
            </Button>
          </div>
        </TabsContent>

        {/* Integrations Tab */}
        <TabsContent value="integrations">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Beehiiv */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-500 flex items-center justify-center text-white font-bold text-xs">B</div>
                    <div>
                      <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Beehiiv</h3>
                      <p className="text-xs text-muted-foreground">Newsletter analytics</p>
                    </div>
                  </div>
                  {form.beehiiv_api_key && (
                    <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Connected</Badge>
                  )}
                </div>
                {form.beehiiv_api_key && !beehiivEditing ? (
                  <div className="space-y-3">
                    <div className="p-3 rounded-lg bg-emerald-50/50 border border-emerald-100">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        <span className="text-xs font-medium text-emerald-800">API Key configured</span>
                      </div>
                      <p className="text-xs text-muted-foreground">Publication: {form.beehiiv_pub_id || 'Not set'}</p>
                    </div>
                    <Button variant="outline" size="sm" className="w-full" onClick={() => setBeehiivEditing(true)}>
                      Reconnect / Update Credentials
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">API Key</Label>
                      <Input
                        type="password"
                        value={form.beehiiv_api_key || ''}
                        onChange={e => setForm({ ...form, beehiiv_api_key: e.target.value })}
                        placeholder="Enter Beehiiv API key"
                        data-testid="beehiiv-api-key"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Publication ID</Label>
                      <Input
                        value={form.beehiiv_pub_id || ''}
                        onChange={e => setForm({ ...form, beehiiv_pub_id: e.target.value })}
                        placeholder="pub_xxxxxxxx"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground">Get from Beehiiv → Settings → Integrations → API</p>
                    {form.beehiiv_api_key && (
                      <Button variant="ghost" size="sm" className="text-xs" onClick={() => setBeehiivEditing(false)}>Cancel</Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Facebook Ads */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-[#1877F2] flex items-center justify-center text-white font-bold text-xs">f</div>
                  <div>
                    <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Facebook Ads</h3>
                    <p className="text-xs text-muted-foreground">Paid campaign metrics</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Ad Account ID</Label>
                    <Input
                      value={form.fb_ad_account_id || ''}
                      onChange={e => setForm({ ...form, fb_ad_account_id: e.target.value })}
                      placeholder="act_xxxxxxxxx"
                    />
                  </div>
                  <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-[10px] text-amber-800">Requires a connected Facebook Ads account in Metricool. Spend, clicks and campaign performance are imported without the direct Meta token.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Google Ads */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-[#4285F4] flex items-center justify-center text-white font-bold text-xs">G</div>
                  <div>
                    <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Google Ads</h3>
                    <p className="text-xs text-muted-foreground">Search & display campaigns</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Customer ID</Label>
                    <Input
                      value={form.google_ads_customer_id || ''}
                      onChange={e => setForm({ ...form, google_ads_customer_id: e.target.value })}
                      placeholder="xxx-xxx-xxxx"
                    />
                  </div>
                  <div className="p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-[10px] text-amber-800">Requires Google Ads API access. You'll need a Google Ads developer token and MCC account to pull campaign data.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* El Toro - Upload */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center text-white font-bold text-xs">ET</div>
                  <div>
                    <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>El Toro</h3>
                    <p className="text-xs text-muted-foreground">IP targeting reports</p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="border-2 border-dashed rounded-xl p-6 text-center hover:bg-muted/30 transition-colors cursor-pointer"
                    onClick={() => document.getElementById(`eltoro-upload-${clientId}`)?.click()}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mx-auto text-muted-foreground mb-2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" x2="12" y1="3" y2="15"/></svg>
                    <p className="text-sm font-medium text-muted-foreground">Upload El Toro Report</p>
                    <p className="text-xs text-muted-foreground mt-1">PDF or CSV</p>
                    <input id={`eltoro-upload-${clientId}`} type="file" accept=".pdf,.csv,.xlsx" className="hidden" onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        toast.success(`El Toro report "${file.name}" uploaded — will be attached to next report`);
                      }
                    }} />
                  </div>
                  <p className="text-[10px] text-muted-foreground">No API available — upload reports manually. They'll be appended to generated reports.</p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-6 flex justify-end">
            <Button onClick={handleSave} disabled={saving} style={{ background: '#1f2a4d' }}>
              {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Save size={14} className="mr-2" />}
              Save Integrations
            </Button>
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Client Users
                </h3>
                <Button size="sm" onClick={() => setAddUserOpen(true)} style={{ background: '#1f2a4d' }}>
                  <UserPlus size={14} className="mr-2" /> Add User
                </Button>
              </div>

              {users.length === 0 ? (
                <div className="text-center py-10">
                  <Users size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                  <p className="text-sm text-muted-foreground mb-2">No users assigned to this client</p>
                  <p className="text-xs text-muted-foreground">Add users so they can log in and view this client's dashboard</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {users.map(u => (
                    <div key={u._id} className="flex items-center justify-between p-3 rounded-lg border bg-white">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#1f2a4d]/10 flex items-center justify-center text-sm font-semibold" style={{ color: '#1f2a4d' }}>
                          {u.full_name?.[0] || u.username?.[0]}
                        </div>
                        <div>
                          <div className="text-sm font-medium">{u.full_name}</div>
                          <div className="text-xs text-muted-foreground">{u.username}</div>
                        </div>
                      </div>
                      <Badge className={u.role === 'client_admin' ? 'bg-red-50 text-red-800 border border-red-100' : 'bg-slate-50 text-slate-700 border border-slate-200'}>
                        {u.role === 'client_admin' ? 'Client Admin' : 'Client Viewer'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* UTMs Tab */}
        <TabsContent value="utms">
          <ClientUTMTab clientId={clientId} clientName={client?.name} websiteUrl={client?.website_url || ''} />
        </TabsContent>
      </Tabs>

      {/* Add User Dialog */}
      <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add User to {client?.name}</DialogTitle>
            <p className="text-sm text-muted-foreground">This user will be able to log in and view this client's data</p>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input value={newUser.full_name} onChange={e => setNewUser({ ...newUser, full_name: e.target.value })} placeholder="John Smith" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={newUser.email} onChange={e => setNewUser({ ...newUser, email: e.target.value, username: e.target.value })} placeholder="john@company.com" />
              <p className="text-[10px] text-muted-foreground">Used for login and password reset</p>
            </div>
            <div className="space-y-2">
              <Label>Temporary Password *</Label>
              <Input type="password" value={newUser.password} onChange={e => setNewUser({ ...newUser, password: e.target.value })} placeholder="Min 6 characters" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newUser.role} onValueChange={v => setNewUser({ ...newUser, role: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client_admin">Client Admin</SelectItem>
                  <SelectItem value="client_viewer">Client Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddUserOpen(false)}>Cancel</Button>
            <Button onClick={handleAddUser} disabled={!newUser.email || !newUser.password || !newUser.full_name} style={{ background: '#1f2a4d' }}>
              Add User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
